/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '@/order/api';
import { OrderDataModel } from '@/order/data/models';
import { transformPlaceOrder } from '@/order/data/transforms';
import { publishPlaceOrderEvent } from '@/order/lib/acdl';
import { handleFetchError } from '@/order/lib/fetch-error';
import { handleNetworkError } from '@/order/lib/network-error';
import { handlePlaceOrderError } from '@/order/lib/place-order-error';
import { events } from '@adobe-commerce/event-bus';
import { SET_PAYMENT_METHOD_AND_PLACE_ORDER_MUTATION } from './graphql/setPaymentMethodAndPlaceOrderMutation';

export const setPaymentMethodAndPlaceOrder = async (
  cartId: string,
  paymentMethod: any
): Promise<OrderDataModel | null | undefined> => {
  if (!cartId) throw new Error('No cart ID found');
  if (!paymentMethod) throw new Error('No payment method found');

  return fetchGraphQl(SET_PAYMENT_METHOD_AND_PLACE_ORDER_MUTATION, {
    variables: { cartId, paymentMethod },
  })
    .then((response: any) => {
      if (response.errors?.length) {
        handleFetchError(response.errors);
      }

      // Check for errors in placeOrder part of the response
      if (response.data?.placeOrder?.errors?.length) {
        handlePlaceOrderError(response.data.placeOrder?.errors);
      }

      // Use the same transformation as placeOrder
      const payload = transformPlaceOrder({ data: { placeOrder: response.data?.placeOrder } });

      if (payload) {
        events.emit('order/placed', payload);
        events.emit('cart/reset', undefined);
        publishPlaceOrderEvent(cartId, payload);
      }

      return payload;
    })
    .catch(handleNetworkError);
};
